'use client';

import { CheckIcon, EyeIcon, EyeOffIcon, XIcon } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { Button } from '@repo/ux/button';
import { Input } from '@repo/ux/input';
import { PASSWORD_REQUIREMENTS } from '@/lib/password-rules';
import { cn } from '@/lib/utils';

interface PasswordStrengthInputProps {
  register: UseFormRegisterReturn;
  label?: string;
  error?: FieldError;
}

const InputPasswordStrengthDemo = ({
  error,
  register,
  label,
}: PasswordStrengthInputProps) => {
  const [password, setPassword] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const id = useId();

  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  const strength = PASSWORD_REQUIREMENTS.map((req) => ({
    met: req.met(password),
    text: req.label,
  }));

  const strengthScore = useMemo(() => {
    return strength.filter((req) => req.met).length;
  }, [strength]);

  const getColor = (score: number) => {
    if (score === 0) return 'bg-border';
    if (score <= 1) return 'bg-destructive';
    if (score <= 2) return 'bg-orange-500 ';
    if (score <= 3) return 'bg-amber-500';
    if (score === 4) return 'bg-yellow-400';

    return 'bg-green-500';
  };

  const getText = (score: number) => {
    if (score === 0) return 'Enter a password';
    if (score <= 2) return 'Weak';
    if (score <= 3) return 'Medium';
    if (score === 4) return 'Strong';

    return 'Very strong';
  };

  return (
    <div className="w-full space-y-2">
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-sm font-medium dark:text-white"
        >
          {label}
        </label>
      )}
      <div className="relative mb-3">
        <Input
          id={id}
          type={isVisible ? 'text' : 'password'}
          placeholder="Password"
          aria-invalid={error ? true : undefined}
          aria-describedby={error?.message ? `${id}-error` : undefined}
          {...register}
          onChange={(e) => {
            register.onChange(e);
            setPassword(e.target.value);
          }}
          className={cn(
            'h-auto py-3 rounded-lg',
            'focus-visible:border-spkblue-base_color focus-visible:ring-spkblue-base_color',
            {
              'border-red-500 focus:border-red-500 focus:ring-red-500': error,
            }
          )}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={toggleVisibility}
          className="text-muted-foreground focus-outline:hidden focus-visible:ring-0 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent top-1"
        >
          {isVisible ? <EyeOffIcon /> : <EyeIcon />}
          <span className="sr-only">
            {isVisible ? 'Hide password' : 'Show password'}
          </span>
        </Button>
      </div>
      {error?.message && (
        <p id={`${id}-error`} role="alert" className="text-sm text-red-600">
          {error.message}
        </p>
      )}
      <div className="mb-4 flex h-1 w-full gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <span
            key={index}
            className={cn(
              'h-full flex-1 rounded-full transition-all duration-500 ease-out',
              index < strengthScore ? getColor(strengthScore) : 'bg-border'
            )}
          />
        ))}
      </div>
      <p className="text-foreground text-sm">
        Level: <span className="font-medium">{getText(strengthScore)}</span>.
      </p>
      <p className="text-foreground text-sm font-medium">
        Your password must contain:
      </p>
      <ul className="mb-4 space-y-1.5">
        {strength.map((req, index) => (
          <li key={index} className="flex items-center gap-2">
            {req.met ? (
              <CheckIcon className="size-4 text-green-600 dark:text-green-400" />
            ) : (
              <XIcon className="text-muted-foreground size-4" />
            )}
            <span
              className={cn(
                'text-xs',
                { 'text-green-600 dark:text-green-400': req.met },
                { 'text-red-500 dark:text-red-400': !req.met && error },
                { 'text-muted-foreground': !error && !req.met }
              )}
            >
              {req.text}
              <span className="sr-only">
                {req.met ? ' - Requirement met' : ' - Requirement not met'}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default InputPasswordStrengthDemo;
